import { faTimes } from '@fortawesome/pro-regular-svg-icons'
import { orderBy } from 'lodash'
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWDropdown, {
  DropdownValueProps,
  GenericDropdownValue,
} from 'shared/components/thunderbolt/IWDropdown'
import IWGenericCard from 'shared/components/thunderbolt/IWGenericCard'
import IWTextInput, {
  IWFileInput,
} from 'shared/components/thunderbolt/IWTextInput'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import IWToggle from 'shared/components/thunderbolt/IWToggle'
import IWTypography from 'shared/components/thunderbolt/IWTypography'
import {
  createOrgCredential,
  getLsAdminConfigs,
  OrgCredential,
} from 'shared/loadSchedulingClient'
import styled from 'styled-components'

export type CreateOrgCredential = Pick<
  OrgCredential,
  | 'configId'
  | 'zone'
  | 'location'
  | 'locationName'
  | 'userId'
  | 'userAccount'
  | 'userPassword'
  | 'pfx'
  | 'pfxPassphrase'
  | 'isSuma'
  | 'isActive'
>

const Wrapper = styled.div`
  display: flex;
  justify-content: left;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 20rem;
`

const StyledSectionRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: left;
  flex-direction: column;
  margin: 0 auto 0 0;

  & > * {
    flex: 0 0 calc(25% - 0.75rem); /* 25% width for 4 items, accounting for gap */
  }
`

const StyledFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 0.5rem;
`

const PreferencesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const CardContent = styled.div`
  display: flex;
  padding: 1rem;
  flex-direction: column;
  gap: 1rem;
`

const StyledInfoBlock = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: 0.5rem;
`

const StyledInfoSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`

const Section = ({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) => {
  return (
    <PreferencesSection>
      {title && (
        <IWTypography size="lg" weight="medium">
          {title}
        </IWTypography>
      )}
      <IWGenericCard>
        <CardContent>
          <StyledInfoSection>
            <StyledInfoBlock>{children}</StyledInfoBlock>
          </StyledInfoSection>
        </CardContent>
      </IWGenericCard>
    </PreferencesSection>
  )
}

export default function LsAdminCreateCredential() {
  const toast = useToast()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const filesRef = useRef<HTMLInputElement | null>(null)
  const [configOptions, setConfigOptions] = useState<
    GenericDropdownValue<string>[]
  >([])
  const [selectedConfig, setSelectedConfig] = useState<DropdownValueProps>()
  const [zone, setZone] = useState('')
  const [location, setLocation] = useState('')
  const [locationName, setLocationName] = useState('')
  const [userId, setUserId] = useState('')
  const [userAccount, setUserAccount] = useState('')
  const [userSubAccount, setUserSubAccount] = useState('')
  const [userPassword, setUserPassword] = useState('')
  const [pfx, setPFX] = useState('')
  const [pfxPassphrase, setPFXPassphrase] = useState('')
  const [isSuma, setIsSuma] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [, setPfxFile] = useState<File>()
  const [isLoading, setIsLoading] = useState(false)

  const { data: configs } = useQuery({
    queryKey: ['ls-admin-configs'],
    queryFn: async () => {
      return getLsAdminConfigs()
    },
  })

  const handleNavigateBack = () => {
    navigate('/admin/load-scheduling/credentials')
  }

  const hasErrors = () => {
    if (!selectedConfig) {
      return true
    }

    if (!zone || !zone.length) {
      return true
    }

    if (!location || !location.length) {
      return true
    }

    if (!locationName || !locationName.length) {
      return true
    }

    return false
  }

  const handleCreate = async () => {
    if (hasErrors()) {
      return
    }

    try {
      setIsLoading(true)

      const credential = {
        configId: selectedConfig?.value,
        zone,
        location,
        locationName,
        userId,
        userAccount,
        userPassword,
        userSubAccount,
        pfx,
        pfxPassphrase,
        isSuma,
        isActive,
      } as CreateOrgCredential

      await createOrgCredential(credential)

      setIsLoading(false)

      handleNavigateBack()
    } catch (error) {
      toast.alert(error?.response?.data, {
        canDismiss: true,
        duration: 10_000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const clearInputFiles = (): void => {
    if (filesRef?.current) {
      filesRef.current.value = ''
    }
    setPfxFile(undefined)
    setPFX('')
  }

  const onFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    const files: File[] = event && Array.from(event.target.files || [])
    const hasFiles = Boolean(files?.length > 0)

    if (!hasFiles) {
      clearInputFiles()
    }

    const [file] = files
    setPfxFile(file)

    const reader = new FileReader()
    reader.onload = () => {
      const base64String = reader.result?.toString().split(',')[1] || ''
      setPFX(base64String)
    }
    reader.readAsDataURL(file)
  }

  useEffect(() => {
    if (configs && configs.length) {
      const configDropdownValues = orderBy(configs, ['market', 'brand'])
        .filter((c) => c.isActive)
        .map((c) => ({
          label: `${c.market} - ${c.brand}`,
          value: c.configId,
        }))

      setConfigOptions(configDropdownValues)
      setSelectedConfig(configDropdownValues[0])
    }
  }, [configs])

  return (
    <main>
      <Wrapper>
        <Section title={t('loadScheduling.labels.configuration')}>
          <StyledSectionRow>
            <IWDropdown
              required
              label={t('loadScheduling.labels.orgConfig')}
              options={configOptions}
              value={selectedConfig}
              disabled={configOptions.length === 0}
              onChange={(value: DropdownValueProps) => {
                setSelectedConfig(value)
              }}
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.pnodeInfo')}>
          <StyledSectionRow>
            <IWTextInput
              label={t('loadScheduling.labels.zone')}
              required
              hasError={!zone || !zone.length}
              value={zone}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setZone(e.target.value)
              }
            />

            <IWTextInput
              label={t('loadScheduling.labels.locationName')}
              required
              hasError={!locationName || !locationName.length}
              value={locationName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setLocationName(e.target.value)
              }
            />

            <IWTextInput
              label={t('loadScheduling.labels.location')}
              required
              hasError={!location || !location.length}
              value={location}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setLocation(e.target.value)
              }
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.credentials')}>
          <StyledSectionRow>
            <IWTextInput
              label={t('loadScheduling.labels.userId')}
              value={userId}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserId(e.target.value)
              }
            />

            <IWTextInput
              label={t('loadScheduling.labels.userAccount')}
              value={userAccount}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserAccount(e.target.value)
              }
            />

            <IWTextInput
              label={t('loadScheduling.labels.userSubAccount')}
              value={userSubAccount}
              placeholder="Default"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserSubAccount(e.target.value)
              }
            />

            <IWTextInput
              label={t('loadScheduling.labels.userPassword')}
              value={userPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserPassword(e.target.value)
              }
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.certificate')}>
          <StyledSectionRow>
            <IWFileInput
              ref={filesRef}
              label={t('loadScheduling.labels.uploadPFX')}
              type="file"
              multiple={false}
              name="file"
              accept=".pfx"
              trailingIcon={{
                icon: faTimes,
                onIconClick: () => clearInputFiles(),
              }}
              onChange={onFileSelect}
            />

            <IWTextInput
              label={t('loadScheduling.labels.PFXPassphrase')}
              value={pfxPassphrase}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPFXPassphrase(e.target.value)
              }
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.settings')}>
          <IWToggle
            id="ls-new-cred-is-suma"
            label={t('loadScheduling.labels.isSUMA')}
            checked={isSuma}
            onChange={() => {
              setIsSuma((p) => !p)
            }}
          />

          <IWToggle
            id="ls-new-cred-is-active"
            label={t('loadScheduling.labels.isActive')}
            checked={isActive}
            onChange={() => {
              setIsActive((p) => !p)
            }}
          />
        </Section>

        <StyledFooter>
          <IWButton color="grey" variant="outline" onClick={handleNavigateBack}>
            {t('button.cancel')}
          </IWButton>

          <IWButton onClick={handleCreate} disabled={hasErrors() || isLoading}>
            {t('button.create')}
          </IWButton>
        </StyledFooter>
      </Wrapper>
    </main>
  )
}
